<template>
  <div
    class="progress-bar progress-bar-label-quit"
    :class="[{ animate: animate }, className]"
  >
    <div class="progress-bar-graphic">
      <div class="progress-bar-graphic-background"></div>
      <div class="progress-bar-graphic-target"></div>
    </div>
    <div v-if="showLabel && !error" class="label">
      {{ count }} / {{ total }}
    </div>
    <div v-if="showLabel && error" class="label">
      <icon
        class="icon-error"
        name="carbon:connection-signal-off"
        size="2rem"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
interface Props {
  size?: number;
  stroke?: number;
  theme?:
    | 'primary'
    | 'secondary'
    | 'error'
    | 'neutral'
    | 'quaternary'
    | 'success';
  opacity?: number;
  count: number;
  total: number;
  showLabel?: boolean;
  showQuit?: boolean;
  error?: boolean;
  isStepDone?: boolean;
}

const props = defineProps({
  size: { default: 10 },
  stroke: { default: 3 },
  theme: { default: 'primary' },
  opacity: { default: 1 },
  count: null,
  total: null,
  showLabel: { type: Boolean, default: true },
  showQuit: { type: Boolean, default: true },
  error: { type: Boolean, default: false },
  isStepDone: { type: Boolean, default: true }
});

const emit = defineEmits(["quit"]);

const animate = ref(false);

const offset = computed(() => (props.isStepDone ? 0 : 1));

const position = computed(
  () => `-${(1 - (props.count - offset.value) / props.total) * 100}%`
);

const handleQuit = () => {
  emit('quit');
};

const className = computed(() => {
  if (props.showLabel && props.showQuit)
    return `progress-bar-by-3 theme-progress-${props.theme}`;
  if (props.showLabel || props.showQuit)
    return `progress-bar-by-2 theme-progress-${props.theme}`;
  return `progress-bar-by-1 theme-progress-${props.theme}`;
});

watch(
  () => props.count,
  (to, from) => {
    if (typeof from !== 'undefined') animate.value = true;
  },
  { immediate: true }
);
</script>
<style lang="scss" scoped>
.progress-bar {
  --color_background: var(--color_neutral_100);
  --color_solid: var(--color_primary);
  --color_border: var(--color_primary);
  --color: var(--color_primary);

  &.theme-progress-error {
    --color_background: var(--color_secondary_lighter);
    --color_solid: var(--color_error);
    --color_border: var(--color_error);
    --color: var(--color_error);
  }
  &.theme-progress-secondary {
    --color_background: var(--color_secondary_lighter);
    --color_solid: var(--color_secondary);
    --color_border: var(--color_secondary);
    --color: var(--color_secondary);
  }
  &.theme-progress-neutral {
    --color_background: var(--color_neutral_500);
    --color_solid: var(--color_neutral_50);
    --color_border: var(--color_neutral_50);
    --color: var(--color_neutral_50);
  }
  &.theme-progress-quaternary {
    --color_background: var(--color_secondary_lighter);
    --color_solid: var(--color_quaternary);
    --color_border: var(--color_quaternary);
    --color: var(--color_quaternary);
  }
  &.theme-progress-success {
    --color_background: var(--color_secondary_lighter);
    --color_solid: var(--color_success);
    --color_border: var(--color_success);
    --color: var(--color_success);
  }
}
.progress-bar-by-3 {
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  width: 100%;
  gap: var(--gap_m);
}
.progress-bar-by-2 {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: var(--gap_m);
  width: 100%;
}
.progress-bar-by-1 {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--gap_m);
  width: 100%;
}
.progress-bar-graphic {
  position: relative;
  align-self: center;
  width: 100%;
  height: 1rem;
  overflow: hidden;
  border-radius: var(--stroke_radius_n);
  &-background,
  &-target {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: var(--stroke_radius_n);
  }
  &-background {
    background: var(--color_background);
  }
  &-target {
    transform: translateX(v-bind(position));
    transform-origin: 0%;
    background: var(--color_solid);
    .animate & {
      transition: transform 600ms $ease-out-quad;
    }
  }
}

.label {
  // width: 6rem;
  color: var(--color_solid);
  font-family: var(--font_family_secondary);
  font-size: var(--text_size_s);
  font-weight: var(--font_weight_700);
  text-align: right;
  @include above('m') {
    font-size: var(--text_size_n);
  }
  @include above('l') {
    font-size: var(--text_size_m);
  }
}

button.quit {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  transition: border 400ms $ease-out-quad, color 200ms $ease-out-quad;
  border: var(--stroke_thickness_l) solid var(--color_border);
  border-radius: 50%;
  color: var(--color_solid);
  &:hover {
    border: var(--stroke_thickness_l) solid var(--color_background);
    color: var(--color_background);
  }
}
</style>
